/** The library: one card per standard report, grouped by what it is about - ADR-0212 decision 6b.
 *
 * A card is a **button** (a title and two to five words), so the page's body text is the cards' labels
 * and nothing else. Picking one writes `?report=`. A report that cannot be produced is still a card:
 * disabled, with "Not available yet" and the server's reason in its tooltip and to a screen reader,
 * because a report that is absent reads as one that does not exist.
 *
 * No readiness, verdict or regime appears here - these are working tables, not filings.
 */

import {
  CalendarBlank,
  ChartBar,
  Files,
  MapPin,
  SealCheck,
  Stack,
  Storefront,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import type { CatalogueEntry } from "../../api/dataReports";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterDef, FilterState } from "../../components/ui/filters";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { Skeleton } from "../../components/ui/Skeleton";
import { TablePage } from "../../components/ui/TablePage";
import { keepSearch } from "../search";
import type { ScreenSearch } from "../search";
import styles from "./DataReports.module.css";

const GROUP_ICON: Readonly<Record<string, Icon>> = {
  Scope: Stack,
  Site: MapPin,
  Vendor: Storefront,
  Time: CalendarBlank,
  Quality: SealCheck,
  Evidence: Files,
};

const GROUPS = ["Scope", "Site", "Vendor", "Time", "Quality", "Evidence"] as const;

export function ReportLibrary({
  reports,
  loading,
  unknownKey,
}: {
  reports: readonly CatalogueEntry[] | undefined;
  loading: boolean;
  /** A `?report=` that names no report in the catalogue. */
  unknownKey: boolean;
}) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const open = (key: string) =>
    void navigate({
      to: "/data-reports",
      search: (prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), report: key }),
    });

  const defs: FilterDef[] = [
    {
      id: "group",
      label: "Group",
      mode: "single",
      options: GROUPS.filter((group) => reports?.some((report) => report.group === group)).map((group) => ({
        value: group,
        label: group,
      })),
    },
  ];
  const wanted = filters.values.group?.[0];
  const needle = filters.search.trim().toLowerCase();
  const shown = (reports ?? []).filter(
    (report) =>
      (!wanted || report.group === wanted) &&
      (!needle || `${report.title} ${report.summary}`.toLowerCase().includes(needle)),
  );

  return (
    <TablePage
      icon={ChartBar}
      title="Reports"
      label="Library"
      actions={<InfoPopover infoKey="reports.library" />}
      filters={
        <FilterLine
          filters={defs}
          state={filters}
          onChange={setFilters}
          searchLabel="Search reports"
          resultCount={reports ? { shown: shown.length, total: reports.length, noun: "reports" } : undefined}
        />
      }
      table={
        loading ? (
          <div className={styles.cards} aria-busy="true">
            <span className="visuallyHidden" role="status">
              Loading…
            </span>
            {[0, 1, 2, 3, 4, 5].map((at) => (
              <Skeleton key={at} />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <EmptyState title="No report matches" compact testId="reports-none" />
        ) : (
          <ul className={styles.cards} data-testid="report-library">
            {shown.map((report) => {
              const Glyph = GROUP_ICON[report.group] ?? ChartBar;
              return (
                <li key={report.key}>
                  <button
                    type="button"
                    className={styles.card}
                    data-report={report.key}
                    data-available={report.available}
                    disabled={!report.available}
                    title={report.why_not ?? undefined}
                    onClick={() => open(report.key)}
                  >
                    <span className={styles.cardIcon} aria-hidden="true">
                      <Glyph size={20} weight="duotone" />
                    </span>
                    <span className={styles.cardText}>
                      <span className={styles.cardTitle}>{report.title}</span>
                      <span className={styles.cardSummary}>
                        {report.available ? report.summary : "Not available yet"}
                      </span>
                    </span>
                    <span className={styles.cardGroup}>{report.group}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )
      }
      state={
        unknownKey && !loading ? (
          <EmptyState title="No such report" compact testId="reports-unknown" />
        ) : undefined
      }
    />
  );
}
